import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, LogOut } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FButton } from "@/components/forkd/FButton";
import { FInput } from "@/components/forkd/FInput";
import { onboarding } from "@/lib/onboarding";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const PRIVACY = ["public", "friends_only", "private"] as const;
type Privacy = (typeof PRIVACY)[number];

type ProfileRow = {
  id: string;
  username: string;
  display_name: string;
  bio: string | null;
  avatar_url: string | null;
  privacy_setting: Privacy;
};

export default function Settings() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [privacy, setPrivacy] = useState<Privacy>("public");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, username, display_name, bio, avatar_url, privacy_setting")
        .eq("id", user.id)
        .maybeSingle();
      if (data) {
        const p = data as ProfileRow;
        setProfile(p);
        setUsername(p.username);
        setDisplayName(p.display_name);
        setBio(p.bio ?? "");
        setAvatarUrl(p.avatar_url);
        setPrivacy(p.privacy_setting);
      }
    })();
  }, [user]);

  const checkUsername = async () => {
    const u = username.trim().toLowerCase();
    if (!u || u === profile?.username) {
      setUsernameError(null);
      return;
    }
    if (!USERNAME_RE.test(u)) {
      setUsernameError("3–20 chars, lowercase letters, numbers, underscores");
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", u)
      .maybeSingle();
    setUsernameError(data ? "Username is already taken" : null);
  };

  const uploadAvatar = async (file: File) => {
    if (!user) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG or PNG");
      return;
    }
    setUploading(true);
    const ext = file.type === "image/png" ? "png" : "jpg";
    const path = `${user.id}/avatar-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) {
      setUploading(false);
      toast.error(upErr.message);
      return;
    }
    const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
    setAvatarUrl(pub.publicUrl);
    await supabase
      .from("profiles")
      .update({ avatar_url: pub.publicUrl })
      .eq("id", user.id);
    setUploading(false);
    toast.success("Avatar updated");
  };

  const save = async () => {
    if (!user) return;
    if (usernameError) return;
    const u = username.trim().toLowerCase();
    if (!USERNAME_RE.test(u)) {
      setUsernameError("3–20 chars, lowercase letters, numbers, underscores");
      return;
    }
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        username: u,
        display_name: displayName.trim(),
        bio: bio.trim() || null,
        privacy_setting: privacy,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Profile saved");
    nav("/profile");
  };

  const handleSignOut = async () => {
    await signOut();
    onboarding.reset();
    nav("/signin", { replace: true });
  };

  return (
    <div>
      <header className="flex items-center justify-between px-4 h-14">
        <Link
          to="/profile"
          aria-label="Back"
          className="w-11 h-11 flex items-center justify-center rounded-[var(--radius-full)]"
        >
          <ArrowLeft size={22} />
        </Link>
        <h1 className="text-[16px] font-semibold">Settings</h1>
        <span className="w-11" />
      </header>

      <section className="px-5 pt-2 pb-6 flex flex-col items-center">
        <button
          onClick={() => fileInput.current?.click()}
          className="relative rounded-[var(--radius-full)] p-[3px] bg-[var(--colour-accent)]"
          aria-label="Change avatar"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="w-24 h-24 rounded-[var(--radius-full)] object-cover bg-[var(--colour-surface)]"
            />
          ) : (
            <div className="w-24 h-24 rounded-[var(--radius-full)] bg-[var(--colour-surface-raised)] flex items-center justify-center text-[32px] font-semibold text-[var(--colour-text-secondary)]">
              {displayName.slice(0, 1).toUpperCase() || "?"}
            </div>
          )}
          <span className="absolute bottom-0 right-0 w-8 h-8 rounded-[var(--radius-full)] bg-[var(--colour-primary)] flex items-center justify-center border-2 border-[var(--colour-background)]">
            <Camera size={16} className="text-[var(--colour-text-inverse)]" />
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadAvatar(f);
            e.target.value = "";
          }}
        />
        {uploading && (
          <p className="mt-2 text-[12px] text-[var(--colour-text-secondary)]">
            Uploading…
          </p>
        )}
      </section>

      <section className="px-4 space-y-3">
        <FInput
          name="displayName"
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <FInput
          name="username"
          label="Username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value.toLowerCase().replace(/\s+/g, ""));
            setUsernameError(null);
          }}
          onBlur={checkUsername}
          error={usernameError}
        />
        <div>
          <label className="block text-[13px] mb-1.5 text-[var(--colour-text-secondary)]">
            Bio
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            maxLength={160}
            className="w-full px-3.5 py-2.5 text-[15px] bg-[var(--colour-surface)] text-[var(--colour-text-primary)] border border-[var(--colour-border)] rounded-[var(--radius-sm)] outline-none focus:border-[var(--colour-accent)]"
          />
        </div>

        <div className="pt-4">
          <label className="block text-[13px] mb-2 text-[var(--colour-text-secondary)]">
            Privacy
          </label>
          <div className="grid grid-cols-3 gap-2">
            {PRIVACY.map((p) => {
              const active = privacy === p;
              return (
                <button
                  key={p}
                  onClick={() => setPrivacy(p)}
                  className="min-h-[44px] rounded-[var(--radius-sm)] border text-[13px] font-semibold capitalize"
                  style={{
                    borderColor: active
                      ? "var(--colour-accent)"
                      : "var(--colour-border)",
                    backgroundColor: active
                      ? "color-mix(in oklab, var(--colour-accent) 8%, var(--colour-surface))"
                      : "var(--colour-surface)",
                    color: active
                      ? "var(--colour-accent)"
                      : "var(--colour-text-primary)",
                  }}
                >
                  {p.replace("_", " ")}
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-4">
          <FButton fullWidth onClick={save} loading={saving}>
            Save changes
          </FButton>
        </div>

        <div className="pt-2">
          <FButton
            variant="outline"
            fullWidth
            onClick={handleSignOut}
            className="text-red-600"
          >
            <LogOut size={18} /> Sign out
          </FButton>
        </div>
      </section>

      <p className="text-center text-[12px] text-[var(--colour-text-secondary)] mt-8 mb-6">
        Forkd v0.1.0
      </p>
    </div>
  );
}
